import { Fragment } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { AboutPageDialogProps } from './AboutPageDialog.types';

/**
 * Explains what a page is for, or shows the detail of a single record. Opened by
 * the info button beside the page title, and the home for the page description
 * the 56px header has no room to show.
 *
 * Rows are data rather than markup because every dialog shares this shape and
 * differs only in its content. A row is a bare sentence on a page's own "about"
 * dialog, or a label over a value where it describes a record.
 */
export function AboutPageDialog(props: AboutPageDialogProps) {
  const { t } = useTranslation('common');

  const hasRows = props.rows.length > 0;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {/* Figma specifies no dialog sizes, so the library's sm/default/lg are
          its own abstraction rather than tokens a design maps onto. The design
          is 480px; it is set once here so all fifteen pages inherit it. */}
      {/* `gap-0` so the 24px above and below the rows is the row block's own
          margin alone — the content's default gap would stack on top of it. */}
      <DialogContent
        className="w-[min(calc(100vw-2rem),30rem)] gap-0"
        data-testid="about-page-dialog"
      >
        {/* The design pairs the title with a 24px line box at weight 500 and
            sits the description 4px under it. The installed DialogTitle ships
            weight 600 on a collapsed line box, and DialogHeader a 6px gap, so
            both are restated here until the design system catches up. */}
        <DialogHeader className="gap-2xs">
          <DialogTitle className="leading-6 font-medium">{props.title}</DialogTitle>
          {props.description && (
            <DialogDescription data-testid="about-page-dialog-description">
              {props.description}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* A page the design gives no rows for keeps the description alone, so
            the row block is dropped rather than left to render 48px of nothing.
            The footer then owns the 24px the block used to contribute. */}
        {hasRows && (
          <div
            data-testid="about-page-dialog-rows"
            className="my-xl flex flex-col type-body-md text-foreground"
          >
            {props.rows.map(({ icon: Icon, label, text }, index) => (
              <Fragment key={label ?? index}>
                {/* 28px clears the icon column (16px icon + 12px gap) so the rule
                    starts where the text does. No semantic step lands on 28. */}
                {index > 0 && (
                  <div data-testid="about-page-dialog-rule" className="ml-7 h-px bg-border-light" />
                )}
                <div className="flex items-start gap-sm py-sm first:pt-0 last:pb-0">
                  <Icon className="mt-3xs size-[16px] shrink-0 text-muted-foreground" />
                  {label ? (
                    <div className="flex min-w-0 flex-col gap-2xs">
                      <span className="type-body-md-strong text-foreground">{label}</span>
                      <span className="text-foreground-alt">{text}</span>
                    </div>
                  ) : (
                    <span>{text}</span>
                  )}
                </div>
              </Fragment>
            ))}
          </div>
        )}

        <DialogFooter className={hasRows ? undefined : 'mt-xl'}>
          {/* The design draws a 40px button, which is the library's `lg` rung. */}
          <Button size="lg" onClick={() => props.onOpenChange(false)}>
            {t('components.aboutPageDialog.gotIt')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
